/**
 * Strikes: a limb, an arc, an aim point, a commitment and the body's motion.
 * The path is solved at throw time; the tip is moved along it every tick and
 * swept against the world by impact.ts. No move list anywhere.
 */
import { Buttons, has, type PlayerInput } from "../input.ts";
import { cos, sin, wrapAngle, yawToRadians } from "../math/trig.ts";
import {
  add,
  copy,
  distance,
  lerp,
  moveToward,
  rotateY,
  scale,
  sub,
  unrotateY,
  type Vec3,
  v3,
} from "../math/vec.ts";
import { eyeLocal, idleFoot, idleHand, leadness, leanOffset } from "../skeleton.ts";
import type {
  Arc,
  ChargeState,
  FighterState,
  Limb,
  MatchState,
  QueuedStrike,
  StrikeState,
  Zone,
} from "../state.ts";
import { DT, TICK_RATE, tuning } from "../tuning.ts";
import { strikeZone, zoneHeight } from "../zones.ts";
import type { SimEvent } from "./events.ts";
import { sweepStrike } from "./impact.ts";
import { isDazed, limbFactors } from "./injuries.ts";
import { poseSeen, rewindTicks } from "./lag.ts";
import { armIntact } from "./movement.ts";

const LIMB_BUTTON: Record<Limb, number> = {
  0: Buttons.ARM_L,
  1: Buttons.ARM_R,
  2: Buttons.LEG_L,
  3: Buttons.LEG_R,
};
const LIMBS: readonly Limb[] = [0, 1, 2, 3];

export function limbKind(limb: Limb): "arm" | "leg" {
  return limb < 2 ? "arm" : "leg";
}

export function limbSide(limb: Limb): -1 | 1 {
  return limb === 0 || limb === 2 ? -1 : 1;
}

/** Current local position of a limb's tip. */
export function limbTip(f: FighterState, limb: Limb): Vec3 {
  return limb === 0 ? f.handL : limb === 1 ? f.handR : limb === 2 ? f.footL : f.footR;
}

export function setLimbTip(f: FighterState, limb: Limb, p: Vec3): void {
  if (limb === 0) f.handL = p;
  else if (limb === 1) f.handR = p;
  else if (limb === 2) f.footL = p;
  else f.footR = p;
}

/** Rest position a limb returns to: the stance guard for hands, the stance for feet. */
export function limbRest(f: FighterState, limb: Limb): Vec3 {
  return limb < 2
    ? idleHand(limbSide(limb), f.duck, f.lean, f.stance)
    : idleFoot(limbSide(limb), f.stance);
}

/**
 * Local root joint of a limb (shoulder or hip), placed like the skeleton does:
 * shoulders slip with the lean and turn with stance and twist, hips turn half
 * as far. A rear cross reaches from a shoulder that has come through.
 */
export function limbRoot(f: FighterState, limb: Limb): Vec3 {
  const b = tuning.body;
  const st = tuning.stance;
  const duckDrop = tuning.movement.duckDrop * f.duck;
  const lean = leanOffset(f.lean);
  const side = limbSide(limb);
  return limb < 2
    ? v3(
        side * b.shoulderX + lean.sway,
        b.shoulderY - duckDrop - lean.drop,
        -side * (f.stance + f.twist) * st.shoulderZ,
      )
    : v3(side * b.hipX, b.hipY - duckDrop, -side * (f.stance + f.twist * 0.5) * st.hipZ);
}

/** 0 for a lead limb .. 1 for a rear limb, from its leadness at the throw. */
export function rearShare(lead: number): number {
  return (1 - lead) / 2;
}

/** Stamina a throw costs: limb, commitment and how much of the body goes with it. */
export function strikeCost(limb: Limb, charge: number, lead: number): number {
  const s = tuning.stamina;
  const c = tuning.chain;
  const base =
    limb < 2
      ? s.strikeTapArm + (s.strikeChargedArm - s.strikeTapArm) * charge
      : s.strikeTapLeg + (s.strikeChargedLeg - s.strikeTapLeg) * charge;
  return base * (c.leadCost + (c.rearCost - c.leadCost) * rearShare(lead));
}

/** How far the trunk turns behind this strike (blade units), from limb, arc and leadness. */
export function trunkTurn(limb: Limb, arc: Arc, lead: number): number {
  const pair = tuning.chain.twist[limbKind(limb)][arc];
  return pair[0] + (pair[1] - pair[0]) * rearShare(lead);
}

/** Effective-mass factor of the kinetic chain: the trunk's turn behind the strike. */
export function chainMass(limb: Limb, arc: Arc, lead: number): number {
  const c = tuning.chain;
  return c.base + (c.gain * trunkTurn(limb, arc, lead)) / c.fullTwist;
}

/** Peak tip speed this limb may reach: commitment, side and injury (m/s). */
export function tipSpeedMax(f: FighterState, limb: Limb, charge: number, lead: number): number {
  const lt = tuning.limbs[limbKind(limb)];
  const c = tuning.chain;
  const side = c.leadSpeed + (c.rearSpeed - c.leadSpeed) * rearShare(lead);
  const top = lt.tipSpeedTap + (lt.tipSpeedCharged - lt.tipSpeedTap) * charge;
  return top * side * f.build.speed * limbFactors(f, limb).speed;
}

/**
 * How long a throw of `pathLength` metres takes with its tip peaking exactly
 * at the limb's speed limit. The progress eases as followThrough·(1 − cos πτ)/2,
 * whose peak rate is followThrough·π/2 per throw. Exact, not rounded to ticks:
 * rounding up made a short path a slower fist.
 */
function throwSeconds(
  f: FighterState,
  s: Pick<StrikeState, "limb" | "charge" | "lead" | "pathLength">,
): number {
  const vmax = tipSpeedMax(f, s.limb, s.charge, s.lead);
  const seconds = (s.pathLength * tuning.followThrough * Math.PI) / (2 * vmax);
  const min = tuning.limbs[limbKind(s.limb)].throwMin * DT;
  return seconds > min ? seconds : min;
}

/** Whole ticks a throw lasts (it ends on the first tick at or past its time). */
function throwTicksFor(seconds: number): number {
  return Math.ceil(seconds * TICK_RATE - 1e-9);
}

/** Length of a quadratic path: close to the mean of its chord and its control polygon. */
function curveLength(origin: Vec3, control: Vec3, aim: Vec3): number {
  return (distance(origin, control) + distance(control, aim) + distance(origin, aim)) / 2;
}

/** Minimum wind-up ticks (a tap) for a limb with this leadness; a quicker build loads sooner. */
function tapTicks(f: FighterState, limb: Limb, lead: number): number {
  const c = tuning.chain;
  const lt = tuning.limbs[limbKind(limb)];
  return Math.round(
    (lt.windupTap * (c.leadWindup + (c.rearWindup - c.leadWindup) * rearShare(lead))) /
      f.build.speed,
  );
}

export function limbReach(limb: Limb): number {
  return limb < 2 ? tuning.limbs.arm.reach : tuning.limbs.leg.reach;
}

/** True when a strike, charge or parry owns this limb's position this tick. */
export function limbControlled(f: FighterState, limb: Limb): boolean {
  if (limb < 2) {
    if (f.strikeArm?.limb === limb || f.chargeArm?.limb === limb) return true;
    if (f.parry && f.parry.arm === limb) return true;
    return false;
  }
  return f.strikeLeg?.limb === limb || f.chargeLeg?.limb === limb;
}

/** True when nothing is loading, in flight or recovering, and no press is waiting. */
function idle(f: FighterState): boolean {
  return (
    f.chargeArm === null &&
    f.chargeLeg === null &&
    f.strikeArm === null &&
    f.strikeLeg === null &&
    f.queue.length === 0
  );
}

/**
 * Whether `limb` may start a strike now: one throw at a time, so nothing may be
 * loading, winding up or still on its way (a strike that has connected is
 * spent: the next one loads as it lands). A punch may start while the other
 * strike follows through or recovers (that hand returns on its own); a kick
 * needs both feet down.
 */
function free(f: FighterState, limb: Limb): boolean {
  if (f.chargeArm !== null || f.chargeLeg !== null) return false;
  for (const s of [f.strikeArm, f.strikeLeg])
    if (s && s.phase !== "recovery" && !(s.phase === "throw" && s.hit)) return false;
  return limb < 2 || f.strikeLeg === null;
}

/**
 * Reads the buttons: presses start a strike or join the combo, charges load
 * and throw on release, covering feints and parries.
 */
export function updateStrikeIntent(state: MatchState, f: FighterState, input: PlayerInput): void {
  if (f.stagger > 0) f.stagger -= 1;
  if (f.initiativeLock > 0) f.initiativeLock -= 1;
  const pressed = input.buttons & ~f.prevButtons;
  const canAct = state.phase === "fight" && f.stagger <= 0;
  if (idle(f)) f.chain = 0;
  // Staggered (or out of the fight), whatever was queued is lost.
  if (!canAct) f.queue.length = 0;

  if (f.guard) {
    // Covering: a charge in progress is aborted (feint), the rest of a combo
    // is dropped, and arm buttons parry.
    if (f.chargeArm) {
      f.chargeArm = null;
      f.stamina = Math.max(0, f.stamina - tuning.stamina.feint);
    }
    if (f.chargeLeg) {
      f.chargeLeg = null;
      f.stamina = Math.max(0, f.stamina - tuning.stamina.feint);
    }
    f.queue.length = 0;
    if (canAct && !f.parry && f.stamina >= tuning.parry.stamina) {
      const arm: 0 | 1 | null = pressed & Buttons.ARM_L ? 0 : pressed & Buttons.ARM_R ? 1 : null;
      if (arm !== null && armIntact(f, arm) && f.strikeArm === null) {
        f.parry = { arm, ticksLeft: tuning.parry.ticks, total: tuning.parry.ticks };
        f.stamina -= tuning.parry.stamina;
      }
    }
    return;
  }
  if (!canAct) return;

  const rewind = rewindTicks(state, input.viewTick);
  // A strike being loaded keeps loading while held and is thrown on release or at full charge.
  const charge = f.chargeArm ?? f.chargeLeg;
  if (charge) {
    const held = (input.buttons & LIMB_BUTTON[charge.limb]) !== 0;
    if (!held || charge.ticks >= tuning.limbs[limbKind(charge.limb)].windupCharged) {
      if (charge.limb < 2) f.chargeArm = null;
      else f.chargeLeg = null;
      throwStrike(state, f, charge, rewind);
    } else charge.ticks += 1;
  }
  // The next link of a combo goes as soon as the previous throw is over. It was
  // committed before anything happened to it, so a block does not hold it back.
  const next = f.queue[0];
  if (next && limbFactors(f, next.limb).disabled) f.queue.shift();
  else if (next && free(f, next.limb)) {
    f.queue.shift();
    beginStrike(state, f, next, input, rewind);
  }
  // A press starts a strike when the fighter is free and has the initiative,
  // or waits its turn in the combo while a throw is on.
  const arc: Arc = has(input, Buttons.ARC_RISING) ? 2 : has(input, Buttons.ARC_AROUND) ? 1 : 0;
  for (const limb of LIMBS) {
    if ((pressed & LIMB_BUTTON[limb]) === 0 || limbFactors(f, limb).disabled) continue;
    // Nothing new until the defender has had its turn, and no more than a full combo.
    if (f.initiativeLock > 0 || f.chain + f.queue.length >= tuning.combo.maxChain) continue;
    const link: QueuedStrike = { limb, arc, zone: strikeZone(limb, input.buttons) };
    if (f.queue.length === 0 && free(f, limb)) beginStrike(state, f, link, input, rewind);
    else f.queue.push(link);
  }
}

/**
 * Starts a strike: it loads while its button is held, and a combo link whose
 * key is already up is thrown at once as a tap. A punch that starts while the
 * other punch recovers takes over the arm channel; that hand returns by itself.
 */
function beginStrike(
  state: MatchState,
  f: FighterState,
  link: QueuedStrike,
  input: PlayerInput,
  rewind: number,
): void {
  f.chain += 1;
  if (link.limb < 2) f.strikeArm = null;
  const charge: ChargeState = { limb: link.limb, arc: link.arc, zone: link.zone, ticks: 0 };
  if ((input.buttons & LIMB_BUTTON[link.limb]) === 0) throwStrike(state, f, charge, rewind);
  else if (link.limb < 2) f.chargeArm = charge;
  else f.chargeLeg = charge;
}

function throwStrike(
  state: MatchState,
  f: FighterState,
  charge: ChargeState,
  rewind: number,
): void {
  const kind = limbKind(charge.limb);
  const lt = tuning.limbs[kind];
  const held = charge.ticks;
  // Lead or rear is read at the throw: a limb thrown mid-switch is in between.
  const lead = leadness(f.stance, limbSide(charge.limb));
  const tap = tapTicks(f, charge.limb, lead);
  const chargeLevel = Math.max(0, Math.min(1, (held - tap) / (lt.windupCharged - tap)));
  f.stamina = Math.max(0, f.stamina - strikeCost(charge.limb, chargeLevel, lead));

  const origin = copy(limbTip(f, charge.limb));
  const aim = aimPointLocal(state, f, limbReach(charge.limb), charge.zone, rewind);
  const control = arcControl(origin, aim, charge.arc, charge.limb);
  // The limb moves at its speed: a short lead path lands sooner, a long hook later.
  const pathLength = curveLength(origin, control, aim);
  const throwTicks = throwTicksFor(
    throwSeconds(f, { limb: charge.limb, charge: chargeLevel, lead, pathLength }),
  );
  const strike: StrikeState = {
    limb: charge.limb,
    arc: charge.arc,
    phase: held < tap ? "windup" : "throw",
    ticks: 0,
    charge: chargeLevel,
    windupTicks: Math.max(0, tap - held),
    throwTicks,
    recoveryTicks: Math.round(lt.recovery / f.build.speed),
    origin,
    control,
    aim,
    aimWorld: toWorld(f, aim),
    rest: limbRest(f, charge.limb),
    end: origin,
    tipPrev: toWorld(f, origin),
    progress: 0,
    pathLength,
    lead,
    hit: false,
    blocked: false,
    whiffed: false,
  };
  if (kind === "arm") f.strikeArm = strike;
  else f.strikeLeg = strike;
}

/**
 * Where a strike at `zone` goes, in fighter space. The look picks the opponent
 * (the one nearest its heading within the aim cone and `reach` plus a little,
 * placed as the player saw them, `rewind` ticks ago) and the line; the zone
 * picks the height. The point is on the look's heading, level with the zone,
 * as far out as the opponent's axis: look beside a slipped head and the fist
 * goes beside it. With nobody there, it goes the limb's reach along the heading.
 */
export function aimPointLocal(
  state: MatchState,
  f: FighterState,
  reach: number,
  zone: Zone,
  rewind = 0,
): Vec3 {
  const eye = add(f.pos, rotateY(eyeLocal(f.duck, f.lean), sin(f.facing), cos(f.facing)));
  const yaw = yawToRadians(f.lookYaw);
  const dx = sin(yaw);
  const dz = cos(yaw);
  const along = aimDistance(state, f, eye, dx, dz, reach + tuning.aim.beyond, rewind);
  const d = along >= 0 ? along : reach;
  return toLocal(f, v3(eye.x + dx * d, f.pos.y + zoneHeight(zone), eye.z + dz * d));
}

/**
 * Distance along the heading (dx, dz) from `eye` to the axis of the opponent
 * nearest that heading within the aim cone and `range`, placed as the fighter
 * saw them `rewind` ticks ago; -1 for nobody.
 */
function aimDistance(
  state: MatchState,
  f: FighterState,
  eye: Vec3,
  dx: number,
  dz: number,
  range: number,
  rewind: number,
): number {
  let bestCos: number = tuning.aim.cone;
  let along = -1;
  for (const o of state.fighters) {
    if (o.id === f.id || !o.alive) continue;
    const p = poseSeen(state, o, rewind).pos;
    const ox = p.x - eye.x;
    const oz = p.z - eye.z;
    const dist = Math.sqrt(ox * ox + oz * oz);
    if (dist > range || dist < 1e-6) continue;
    const c = (ox * dx + oz * dz) / dist;
    if (c <= bestCos) continue;
    bestCos = c;
    along = ox * dx + oz * dz;
  }
  return along;
}

type Frame = Pick<FighterState, "pos" | "facing">;

export function toWorld(f: Frame, local: Vec3): Vec3 {
  return add(f.pos, rotateY(local, sin(f.facing), cos(f.facing)));
}

/** World space into a fighter's local frame (feet at the origin, facing +z). */
export function toLocal(f: Frame, world: Vec3): Vec3 {
  return unrotateY(sub(world, f.pos), sin(f.facing), cos(f.facing));
}

/** Advances charges, strikes and parries; moves the limbs; sweeps the throw. */
export function updateStrikes(
  state: MatchState,
  f: FighterState,
  events: SimEvent[],
  predictOnly: boolean,
): void {
  let twist = 0;
  for (const charge of [f.chargeArm, f.chargeLeg]) {
    if (!charge) continue;
    const kind = limbKind(charge.limb);
    const max = tuning.limbs[kind].windupCharged;
    const rest = limbRest(f, charge.limb);
    const lead = leadness(f.stance, limbSide(charge.limb));
    const load = loadPoint(rest, charge.arc, charge.limb, lead);
    const k = Math.min(1, charge.ticks / max);
    // The limb gets there at its own speed: a combo link starts from wherever the last one left it.
    const speed = kind === "arm" ? tuning.guard.armSpeed : tuning.check.footSpeed;
    const maxStep = speed * f.build.speed * DT;
    setLimbTip(f, charge.limb, moveToward(limbTip(f, charge.limb), lerp(rest, load, k), maxStep));
    twist += twistOf(charge.limb, charge.arc, lead) * -0.2 * k;
  }
  if (f.strikeArm) f.strikeArm = advanceStrike(state, f, f.strikeArm, events, predictOnly);
  if (f.strikeLeg) f.strikeLeg = advanceStrike(state, f, f.strikeLeg, events, predictOnly);
  for (const strike of [f.strikeArm, f.strikeLeg]) if (strike) twist += strikeTwist(strike);
  const target = twist > 2 ? 2 : twist < -2 ? -2 : twist;
  const rate = tuning.chain.twistRate;
  f.twist =
    target - f.twist > rate ? f.twist + rate : f.twist - target > rate ? f.twist - rate : target;
  if (f.parry) {
    const p = f.parry;
    p.ticksLeft -= 1;
    // Out from the guard along the look's heading: the cover already holds the height.
    const rel = wrapAngle(yawToRadians(f.lookYaw) - f.facing);
    const aimDir = v3(sin(rel), 0, cos(rel));
    const push = tuning.parry.push * (isDazed(f) ? tuning.injury.dazedParryFactor : 1);
    // Out and back: a half sine over the push's life.
    const phase = 1 - p.ticksLeft / p.total;
    const amount = push * sin(phase * Math.PI);
    const base = limbTip(f, p.arm);
    setLimbTip(f, p.arm, add(base, scale(aimDir, amount * 0.5)));
    if (p.ticksLeft <= 0) f.parry = null;
  }
}

/**
 * Where a limb coils to before a throw; a rear limb coils further (the hip
 * loads), and a kicking foot chambers: the knee comes up before the leg fires.
 */
function loadPoint(rest: Vec3, arc: Arc, limb: Limb, lead: number): Vec3 {
  const lift = limb < 2 ? 0 : arc === 2 ? tuning.chamber / 3 : tuning.chamber;
  const back = v3(0, lift, -0.15 - tuning.chain.rearLoad * rearShare(lead));
  if (arc === 1) return add(add(rest, back), v3(limbSide(limb) * 0.2, 0, 0));
  if (arc === 2) return add(add(rest, back), v3(0, limb < 2 ? -0.15 : 0, 0));
  return add(rest, back);
}

/**
 * Signed torso turn a strike drives, in blade units: the throwing side comes
 * forward (a cross turns through, a jab reaches).
 */
function twistOf(limb: Limb, arc: Arc, lead: number): number {
  return -limbSide(limb) * trunkTurn(limb, arc, lead);
}

/** Torso turn of a strike at its current phase: coil, turn through, unwind. */
function strikeTwist(s: StrikeState): number {
  const full = twistOf(s.limb, s.arc, s.lead);
  if (s.phase === "windup") return full * -0.2 * Math.min(1, s.ticks / Math.max(1, s.windupTicks));
  if (s.phase === "throw") return full * Math.min(1, s.progress);
  return full * (1 - Math.min(1, s.ticks / Math.max(1, s.recoveryTicks)));
}

function advanceStrike(
  state: MatchState,
  f: FighterState,
  s: StrikeState,
  events: SimEvent[],
  predictOnly: boolean,
): StrikeState | null {
  s.ticks += 1;
  if (s.phase === "windup") {
    const load = loadPoint(s.rest, s.arc, s.limb, s.lead);
    setLimbTip(f, s.limb, lerp(s.origin, load, Math.min(1, s.ticks / Math.max(1, s.windupTicks))));
    if (s.ticks >= s.windupTicks) {
      s.phase = "throw";
      s.ticks = 0;
      s.origin = copy(limbTip(f, s.limb));
      s.control = arcControl(s.origin, s.aim, s.arc, s.limb);
      s.tipPrev = toWorld(f, s.origin);
      // The coil changed the path: plan the throw over the new one.
      s.pathLength = curveLength(s.origin, s.control, s.aim);
      s.throwTicks = throwTicksFor(throwSeconds(f, s));
    }
    return s;
  }
  if (s.phase === "throw") {
    // Track the world-anchored aim (turning re-aims, stepping in shortens the path),
    // but move the limb at its own speed over the distance it planned: a target
    // that came closer is reached sooner, and the step's speed adds to the arm's.
    s.aim = unrotateY(sub(s.aimWorld, f.pos), sin(f.facing), cos(f.facing));
    s.control = arcControl(s.origin, s.aim, s.arc, s.limb);
    const p = s.ticks >= s.throwTicks ? 1 : Math.min(1, (s.ticks * DT) / throwSeconds(f, s));
    const planned = (tuning.followThrough * (1 - cos(p * Math.PI))) / 2;
    const now = curveLength(s.origin, s.control, s.aim);
    const scaled = now > 1e-6 ? (planned * s.pathLength) / now : planned;
    s.progress = scaled < tuning.followThrough ? scaled : tuning.followThrough;
    const tipLocal = bezier(s.origin, s.control, s.aim, s.progress);
    setLimbTip(f, s.limb, tipLocal);
    const tipWorld = toWorld(f, tipLocal);
    if (!s.hit && !predictOnly) {
      const radius = s.limb < 2 ? tuning.body.fistRadius : tuning.body.footRadius;
      if (sweepStrike(state, f, s, s.tipPrev, tipWorld, radius, events)) s.hit = true;
    }
    s.tipPrev = tipWorld;
    if (s.ticks >= s.throwTicks) {
      s.phase = "recovery";
      s.ticks = 0;
      s.end = copy(tipLocal);
      if (!s.hit) {
        s.whiffed = true;
        s.recoveryTicks = Math.round(s.recoveryTicks * tuning.whiffRecoveryFactor);
        // Missing is tiring: the throw's cost again, in part. Prediction never knows it missed.
        if (!predictOnly)
          f.stamina = Math.max(
            0,
            f.stamina - strikeCost(s.limb, s.charge, s.lead) * tuning.stamina.whiff,
          );
      }
    }
    return s;
  }
  // recovery
  setLimbTip(
    f,
    s.limb,
    lerp(s.end, limbRest(f, s.limb), Math.min(1, s.ticks / Math.max(1, s.recoveryTicks))),
  );
  if (s.ticks >= s.recoveryTicks) return null;
  return s;
}

/**
 * The arc's control point. Punches bow sideways (around) or dip (rising). A
 * kick, chambered, travels level with its target: a roundhouse swings in at
 * the aimed height and a push kick drives out along it, so a body kick meets
 * the ribs (and the elbow guarding them) rather than rising into the belt.
 */
function arcControl(origin: Vec3, aim: Vec3, arc: Arc, limb: Limb): Vec3 {
  const mid = lerp(origin, aim, 0.5);
  if (arc === 2) return add(mid, v3(0, -tuning.arcOffset, 0.1));
  if (limb >= 2) {
    const level = v3(mid.x, aim.y, mid.z);
    return arc === 1 ? add(level, v3(limbSide(limb) * tuning.arcOffset, 0, 0)) : level;
  }
  if (arc === 1) return add(mid, v3(limbSide(limb) * tuning.arcOffset, 0.05, 0));
  return mid;
}

function bezier(a: Vec3, c: Vec3, b: Vec3, t: number): Vec3 {
  const u = 1 - t;
  return v3(
    u * u * a.x + 2 * u * t * c.x + t * t * b.x,
    u * u * a.y + 2 * u * t * c.y + t * t * b.y,
    u * u * a.z + 2 * u * t * c.z + t * t * b.z,
  );
}

/** Interrupts a strike after a deflection: straight to recovery with a penalty and a stagger. */
export function deflectStrike(f: FighterState, s: StrikeState): void {
  s.phase = "recovery";
  s.ticks = 0;
  s.end = copy(limbTip(f, s.limb));
  s.hit = true;
  s.recoveryTicks += tuning.parry.attackerRecoveryPenalty;
  f.stagger = tuning.staggerTicks;
  f.initiativeLock = s.recoveryTicks + tuning.initiativeTicks;
  // A parry breaks the combo: the rest of it never comes.
  f.queue.length = 0;
}

export { DT };
